import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingLayout from './OnboardingLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const OnboardingCashX = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [tested, setTested] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [mode, setMode] = useState('skip'); // 'skip' or 'connect'
  const [formData, setFormData] = useState({
    enabled: false,
    base_url: 'https://cashx-api.example.com',
    shared_secret: ''
  });

  const handleTest = async () => {
    if (!formData.base_url || !formData.shared_secret) {
      toast.error('Please fill in all fields');
      return;
    }

    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/onboarding/cashx/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTested(true);
      setTestSuccess(response.data.success);
      if (response.data.success) {
        toast.success('CashX connection successful!');
      } else {
        toast.error('Connection failed - check credentials');
      }
    } catch (error) {
      console.error('Test error:', error);
      setTested(true);
      setTestSuccess(false);
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = mode === 'connect' ? { ...formData, enabled: true } : { enabled: false };
      await axios.post(`${API_URL}/api/onboarding/cashx/connect`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(mode === 'connect' ? 'CashX connected!' : 'CashX skipped');
      navigate('/onboarding/qr-test');
    } catch (error) {
      console.error('CashX connect error:', error);
      toast.error(error.response?.data?.detail || 'Connection failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingLayout currentStep={3}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Link2 className="h-6 w-6" />
            CashX Integration
          </h2>
          <p className="text-gray-500 mt-1">
            Connect Flotto with CashX for delivery management (optional)
          </p>
        </div>

        {/* Mode Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card
            className={`p-6 cursor-pointer border-2 ${
              mode === 'skip' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
            }`}
            onClick={() => setMode('skip')}
          >
            <h3 className="font-semibold text-lg">Standalone Mode</h3>
            <p className="text-sm text-gray-600 mt-2">
              Use Flotto without CashX integration. You can connect later.
            </p>
          </Card>

          <Card
            className={`p-6 cursor-pointer border-2 ${
              mode === 'connect' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
            }`}
            onClick={() => setMode('connect')}
          >
            <h3 className="font-semibold text-lg">Connect CashX</h3>
            <p className="text-sm text-gray-600 mt-2">
              Integrate with CashX for automatic delivery synchronization.
            </p>
          </Card>
        </div>

        {/* Connection Form */}
        {mode === 'connect' && (
          <Card className="p-6 space-y-4">
            <div>
              <Label htmlFor="base_url">CashX API URL</Label>
              <Input
                id="base_url"
                value={formData.base_url}
                onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                placeholder="https://your-cashx-instance.com"
              />
            </div>

            <div>
              <Label htmlFor="shared_secret">Shared Secret</Label>
              <Input
                id="shared_secret"
                type="password"
                value={formData.shared_secret}
                onChange={(e) => setFormData({ ...formData, shared_secret: e.target.value })}
                placeholder="Your CashX integration secret"
              />
              <p className="text-xs text-gray-500 mt-1">
                Find this in your CashX admin panel under Integrations
              </p>
            </div>

            <Button
              onClick={handleTest}
              disabled={testing || !formData.base_url || !formData.shared_secret}
              variant="outline"
              className="w-full"
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing Connection...
                </>
              ) : (
                'Test Connection'
              )}
            </Button>

            {tested && (
              <div
                className={`flex items-center gap-2 p-4 rounded-md ${
                  testSuccess ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                {testSuccess ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span>Connection successful! CashX is ready.</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5" />
                    <span>Connection failed. Please check your credentials.</span>
                  </>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button variant="outline" onClick={() => navigate('/onboarding/drivers')}>
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (mode === 'connect' && !tested) || (mode === 'connect' && !testSuccess)}
          >
            {loading ? 'Saving...' : 'Next: QR Test'}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingCashX;
